import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, fork, put, race, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import {
  DialogueRegisterState,
  IActionRegister,
  INext,
  IRegisterAction,
  actionsComplete,
  next,
  registerActionResult,
} from 'core/slices/register';
import { actionRequest, attemptRequest, completeRequest, dialogueRequest } from 'core/services/product';
import { IActionAction } from 'types/action/player';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { selectToken } from 'core/selectors/product/token';
import { IToken, Mode } from 'core/slices/session';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { IActionFeed, IActionFeedItem, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { IPlayerReducer, ViewerState } from 'core/slices/player';
import { SavingState } from 'types/domain/base';
import { selectCourseNode } from 'core/selectors/admin/courseNode';
import { CourseNodeRole, ICourseNodeDAO } from 'types/domain/course';

function* handleRegister(action: PayloadAction<IRegisterAction>) {
  if (!action.payload.sessionAttemptId) return;

  const mode: Mode = yield select(selectSessionMode);

  if (mode !== Mode.LEARNING) return;

  const actionRegister: IActionRegister = yield select((state: RootState) =>
    state.register.attempts[action.payload.sessionAttemptId]
      ? state.register.attempts[action.payload.sessionAttemptId].actions[action.payload.actionId]
      : null,
  );

  if (!actionRegister || !action.payload.actionId) return console.warn('No action register');

  if (!action.payload.result) return;

  let sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  if (sessionAttemptId < 0) {
    const { payload } = yield take(attemptRequest.fulfilled);
    sessionAttemptId = payload.id;
  }

  const register: IActionAction = {
    ...action.payload,
    result: actionRegister.result,
    created: actionRegister.created,
    started: actionRegister.started,
    completed: actionRegister.completed,
    entities: Object.values(actionRegister.entities),
    sessionAttemptId,
  };

  yield put(yield call(actionRequest, register));
}

// How long completing a module waits on its last action's save before going ahead anyway.
const LAST_SAVE_TIMEOUT = 15000;

const selectFeedItemSaving = (state: RootState, item?: IActionFeedItem) =>
  item?.actionId ? state.register.attempts[item.sessionAttemptId]?.actions[item.actionId]?.saving : undefined;

/**
 * The module is completed only once its last action has saved, or the server scores it
 * without that answer. This used to bail out while the save was in flight, which nothing
 * retried: when the save outlasted the answer's feedback delay (a slow API) the auto-next
 * was dropped and the player sat on the last slide. Wait on that action's own register;
 * `loading.action` is shared by every save, and an earlier one landing clears it.
 */
function* waitForLastSave(item: IActionFeedItem) {
  if ((yield select(selectFeedItemSaving, item)) !== SavingState.SAVING) return;

  yield race({ saved: call(untilSaved, item), timedOut: delay(LAST_SAVE_TIMEOUT) });
}

function* untilSaved(item: IActionFeedItem) {
  while ((yield select(selectFeedItemSaving, item)) === SavingState.SAVING)
    noteMovedOn(yield take([actionRequest.fulfilled, actionRequest.rejected]));
}

/**
 * Saves that moved the course on. The API completes a module itself when its last slide is
 * saved (ModuleIsComplete), and answers that save with whatever comes next - so the module
 * must not be completed again on top of it, or the next module is started twice and the
 * learner plays every module twice. Keyed by attempt and action.
 */
const movedOn = new Set<string>();
const saveKey = (sessionAttemptId: number, actionId: number) => `${sessionAttemptId}:${actionId}`;

function noteMovedOn(action) {
  const arg = action?.meta?.arg;
  if (action?.type === actionRequest.fulfilled.type && arg && action.payload?.queue?.length)
    movedOn.add(saveKey(arg.sessionAttemptId, arg.actionId));
}

/**
 * Attempts this tab has asked the API to complete. Completing one moves the course on and
 * starts the next module, and the API can't always tell a repeat from the first (a module's
 * step only becomes the learner's place once they answer in it), so a second request - next
 * pressed again on the last slide before the next module has loaded - started the next
 * module twice, and the learner played it twice. Forgotten if the request fails, to retry.
 */
const completing = new Set<number>();

function* completeAttempt(sessionAttemptId: number, courseProgressId: number) {
  if (completing.has(sessionAttemptId)) return;
  completing.add(sessionAttemptId);

  yield put(yield call(completeRequest, { sessionAttemptId, courseProgressId }));
}

function forgetFailedComplete(action) {
  completing.delete(action.meta?.arg?.sessionAttemptId);
}

function* handleNext(action: PayloadAction<INext>) {
  const mode: Mode = yield select(selectSessionMode);

  if (mode === Mode.LEARNING) {
    const feed: IActionFeed = yield select(selectActionFeed);
    if (feed.feed.length === feed.index + 1) yield waitForLastSave(feed.feed[feed.index]);
  }

  // Re-read after the wait: the learner may have moved on meanwhile.
  const actionFeed: IActionFeed = yield select(selectActionFeed);
  const player: IPlayerReducer = yield select((state: RootState) => state.core.player);
  const sessionAttemptId = yield select((state: RootState) => state.state[EntityTypes.SESSION_ATTEMPT]);
  const token: IToken = yield select(selectToken);

  if (
    player.state.viewer.state !== ViewerState.TRANSITIONING &&
    actionFeed.feed.length === actionFeed.index + 1 &&
    action.payload.interaction
  ) {
    if (mode === Mode.LEARNING) {
      const last = actionFeed.feed[actionFeed.index];

      if (last.actionId && movedOn.has(saveKey(last.sessionAttemptId, last.actionId))) {
        // Its save already completed the module and started what's next; that is on its way.
      } else if (last.actionId || last.feedback) {
        yield completeAttempt(sessionAttemptId, token.courseProgressId);
      } else if (last.courseNodeId) {
        const groupIds = yield select((state: RootState) => Object.keys(state.session.groups));
        const courseNode: ICourseNodeDAO = yield select(state =>
          selectCourseNode(state, actionFeed.feed[actionFeed.index].courseNodeId),
        );
        yield put(
          yield call(dialogueRequest, {
            token: token.token,
            courseNodeId: actionFeed.feed[actionFeed.index].courseNodeId,
            courseProgressId: token.courseProgressId,
            state: DialogueRegisterState.SUBMITTED,
            groupIds: courseNode.entityType === CourseNodeRole.GROUP ? groupIds : [],
            autoNext: true,
          }),
        );
      }

      const nextPage = actionFeed.feed[actionFeed.index + 1];
      if (nextPage?.feedback) {
        yield completeAttempt(sessionAttemptId, token.courseProgressId);
      }
    } else {
      yield put(yield call(actionsComplete));
    }
  }
}

function* watchRegister() {
  yield takeEvery(registerActionResult.type, handleRegister);
}

function* watchNext() {
  // Leading: a second next while the first waits on the last save must not complete twice.
  yield takeLeading(next, handleNext);
}

function* watchSaves() {
  yield takeEvery(actionRequest.fulfilled, noteMovedOn);
}

function* watchCompleteFailed() {
  yield takeEvery(completeRequest.rejected, forgetFailedComplete);
}

export function* requestSaga() {
  yield all([fork(watchRegister), fork(watchNext), fork(watchCompleteFailed), fork(watchSaves)]);
}
